import { FormikHelpers } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useModal } from 'yoso';
import { useBalance } from '~/components/Balance/Balance.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { LevelUpFormik } from '~/components/LevelUp/Form/LevelUpFormik';
import { LevelUpStatus, useLevelUp } from '~/components/LevelUp/LevelUp.hooks';
import { usePurchased } from '~/components/Membership/Membership.hooks';
import './LevelUp.scss';
import { LevelUpConfirmPopup } from './Popup/Confirm/LevelUpConfirmPopup';

export type LevelUpValues = {
  purchase: number;
  balance: number;
  tokenIdList: string[];
  level: number;
};
export type LevelUpProps = {
  level: number;
  onSuccess?: (data: LevelUpStatus) => void;
  onError?: (e: BaseError) => void;
};

export const LevelUp = ({ level, onSuccess, onError }: LevelUpProps) => {
  const { open } = useModal();
  const [enableReinitialize, setEnableReinitialize] = useState<boolean>();

  const {
    data: { amount: balance },
    isFetched: isBalanceFetched,
    refetch: refetchBalance,
  } = useBalance();
  const { data, isFetched: isPurchaseFetched, refetch: refetchPurchased } = usePurchased();
  const {
    purchase: { amount: purchase = 0 },
  } = data || {};

  const initialValues = useMemo(
    () => ({
      level,
      purchase,
      balance,
      tokenIdList: [],
    }),
    [level, purchase, balance]
  );

  useEffect(() => {
    const enableReinitialize = !(isBalanceFetched && isPurchaseFetched);
    setEnableReinitialize(enableReinitialize);
  }, [isBalanceFetched, isPurchaseFetched]);

  const { mutateAsync } = useLevelUp({ onSuccess, onError });

  const onSubmit = async (
    values: LevelUpValues,
    { resetForm, validateForm }: FormikHelpers<LevelUpValues>
  ) => {
    const { confirmed } = await open(<LevelUpConfirmPopup />);
    if (confirmed) {
      await mutateAsync(values);
      const [{ data }] = await Promise.all([refetchBalance(), refetchPurchased()]);
      resetForm({
        values: {
          ...initialValues,
          balance: data?.amount ?? initialValues.balance,
        },
      });
      await validateForm();
    }
  };
  return (
    <LevelUpFormik
      initialValues={initialValues}
      enableReinitialize={enableReinitialize}
      onSubmit={onSubmit}
    />
  );
};
